import { useCallback } from 'react'
import { useUpload as useCommonsUpload } from '@demo-system/commons/hooks/upload'
import { uploadFile as commonsUploadFile } from '@demo-system/commons/service/upload'
import { dsnApiV2, dsnProjectApi } from 'services/axios'

const MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
}

export const guessExt = (mime = '') => MIME_TO_EXT[mime.split(';')[0].trim().toLowerCase()] ?? 'jpg'

// Plain async helper - safe to call from async workers, loops, non-React code.
// Returns { status: 'success' | 'error', url?, path?, message? }.
export const uploadFileToDsnProject = (file, projectId, path) =>
  commonsUploadFile({
    file,
    path,
    getPreSignedUrl: async (p) => {
      const { data } = await dsnProjectApi.get(`${projectId}/upload`, { params: { path: p } })
      return data
    },
  })

// React hook - same thing with stable callback + loading/error state.
// Use inside components that need UI spinners/toasts.
export const useDsnProjectUpload = (projectId) => {
  const getPreSignedUrl = useCallback(
    async (path) => {
      const { data } = await dsnProjectApi.get(`${projectId}/upload`, { params: { path } })
      return data
    },
    [projectId]
  )
  return useCommonsUpload(getPreSignedUrl) // { upload, loading, error, status, preview }
}

// Fetch a DemoPilot asset's original rendition and return it as a browser File.
//
// Scraped import: pass `displayName` only — returns null on failure; File name is
// `${safeName}.${guessExt(blob.type)}` (same as pre-centralization).
//
// Assets gallery: pass `fileName` (e.g. asset.name || … || `${id}.jpg`) and
// `throwOnFailure: true` — throws the same errors as the old inline hook on failure.
export const fetchDemoPilotAssetAsFile = async ({
  demoPilotProjectId,
  assetId,
  displayName,
  fileName: explicitFileName,
  throwOnFailure,
}) => {
  const { data } = await dsnApiV2.get(`/demopilot/projects/${demoPilotProjectId}/assets/${assetId}/rendition`, {
    params: { size: 'original' },
  })

  if (!data?.url) {
    if (throwOnFailure) {
      throw new Error(`Failed to get rendition URL for asset ${assetId}`)
    }
    return null
  }

  const resp = await fetch(data.url, { credentials: 'include' })
  if (!resp.ok || resp.status === 204 || resp.status === 404) {
    if (throwOnFailure) {
      throw new Error(`Failed to fetch rendition bytes for asset ${assetId}`)
    }
    return null
  }

  const blob = await resp.blob()
  if (!blob) {
    if (throwOnFailure) {
      throw new Error(`Failed to fetch original rendition for asset ${assetId}`)
    }
    return null
  }

  let fileName
  if (explicitFileName != null && String(explicitFileName).length > 0) {
    fileName = explicitFileName
  } else {
    const extension = guessExt(blob.type)
    const raw = (displayName || assetId).replace(/[^a-z0-9-_]+/gi, '-')
    const safeName = raw || String(assetId)
    fileName = `${safeName}.${extension}`
  }

  return new File([blob], fileName, { type: blob.type || 'image/jpeg' })
}
